// eslint-disable-next-line import/no-extraneous-dependencies
import DromoUploader from "dromo-uploader-js";
import { ref } from "vue";
import { restClient } from "@/lib/raftApi";
import { useDrupalSettings } from "@/composables/useDrupalSettings";
import { useEnvVars } from "./useEnvVars";
import { useNotification } from "./useNotification";

// eslint-disable-next-line import/prefer-default-export
export function useDromo({ dromoApiKey, dromoSchema }) {
  const { config } = useDrupalSettings();
  const { isProduction } = useEnvVars();
  const dromoResultsData = ref([]);
  const dromoResultsMetadata = ref([]);
  const dromoFinished = ref(false);
  const { showNotification } = useNotification();
  const isBusyValidating = ref(false);

  const dromoOptions = {
    licenseKey: dromoApiKey,
    fields: dromoSchema,
    settings: {
      maxRecords: 10000,
      developmentMode: !isProduction,
      autoMapHeaders: true,
      importIdentifier: "Chemical Containers",
      uploadStep: {
        helpText:
          "Upload a CSV file to add new chemical container records and to edit existing records (up to 10,000).",
      },
      reviewStep: {
        processingText: "Validation in progress, wait before proceeding.",
      },
      templateDownloadFilename: "container-import-template",
      manualInputDisabled: true,
    },
    user: {
      id: "1",
    },
  };

  const dromo = new DromoUploader(
    dromoOptions.licenseKey,
    dromoOptions.fields,
    dromoOptions.settings,
    dromoOptions.user,
  );

  const replaceLineBreaks = (fields, recordToCheck) => {
    fields.forEach((field) => {
      if (recordToCheck.row[field]) {
        if (
          recordToCheck.row[field].value &&
          typeof recordToCheck.row[field].value === "string"
        ) {
          const value = recordToCheck.row[field].value;
          recordToCheck.row[field].value = value.replaceAll(/\n/g, ";");
        }
        if (
          recordToCheck.row[field].resultValue &&
          typeof recordToCheck.row[field].resultValue === "string"
        ) {
          const resultValue = recordToCheck.row[field].resultValue;
          recordToCheck.row[field].resultValue = resultValue.replace(
            /\n/g,
            ";",
          );
        }
      }
    });
    return recordToCheck;
  };

  /**
   * Restructure the chemical container import data for the API.
   *
   * @param {Object[]} containers - The chemical container import data
   * @return {Object[]} Returns the restructured data for the API.
   */
  const restructureImportData = (containers) => {
    // Restructure the data for the API.
    const postData = [];
    containers.forEach((importData) => {
      const item = {};
      Object.keys(importData.row).forEach((key) => {
        item[key] = importData.row[key].value;
      });
      item.rowId = importData.rowId;
      postData.push(item);
    });
    return postData;
  };

  /**
   * Format the validation messages.
   *
   * @param {Object[]} messages Field messages for a single validator row.
   * @return {Object[]} Returns the formatted validation messages.
   */
  const getValidationMessages = (messages) =>
    messages.map((fieldMessage) => ({
      message: fieldMessage.message,
      level: fieldMessage.info,
    }));

  /**
   * Handle the validation API response, update Dromo with messages.
   *
   * @param {Object[]} response - The validation response from the API.
   * @return {Object[]} Returns the formatted validation messages.
   */
  const handleValidationResponse = (response) => {
    const messages = [];
    response?.forEach((item) => {
      Object.keys(item.messages).forEach((key) => {
        const message = {
          rowIndex: item.index,
          fieldKey: key,
          rowId: item.rowId,
        };
        message.messages = getValidationMessages(item.messages[key]);
        messages.push(message);
      });
    });
    return messages;
  };

  /**
   * Validate the chemical container import data.
   *
   * @param {Object[]} containers - The chemical container import data to validate.
   * @param {Boolean} save - Should we ask the API to create containers once validated?
   */
  const validateData = async (containers, save = false) => {
    isBusyValidating.value = true;
    let postData = containers;
    if (!save) {
      postData = restructureImportData(containers);
    }
    const queries = {
      _format: "json",
    };
    if (config?.groupContext) {
      queries["rog-id"] = config.groupContext;
      queries["rog-type"] = "node";
    }
    if (save) {
      queries.validate_and_save = 1;
    }

    const options = {
      path: "/chemical-container-import",
      queries: queries,
      data: { data: postData },
    };
    const response = await restClient.post(options).catch((error) => {
      showNotification("error", error.message);
      return { error };
    });
    isBusyValidating.value = false;
    return response;
  };

  let allRecords = [];

  const findIndex = (rowId) =>
    allRecords.findIndex((item) => item.rowId === rowId);

  /**
   * Validate the chemical container import data.
   *
   * @param {Object[]} records - The chemical container import data to validate.
   * @param {Object[]} apiMessages - Messages from API response after validating and formatting.
   * @return {Object[]} Returns full list of messages with updated messages from API.
   */
  const updateFullListOfMessages = (records, apiMessages) => {
    const fullListOfMessages = [];
    records.forEach((record) => {
      // for field in row
      Object.keys(record.row).forEach((fieldOnRow) => {
        // return messages if found above, else []
        fullListOfMessages.push({
          rowIndex: findIndex(record.rowId),
          fieldKey: fieldOnRow,
          messages:
            apiMessages?.find(
              (item) =>
                item.rowId === record.rowId && item.fieldKey === fieldOnRow,
            )?.messages ?? [],
        });
      });
    });
    return fullListOfMessages;
  };

  /**
   * Check for cas number and chemical name empty error.
   *
   * @param {Object[]} responseData - The validated chemical container import data.
   * @return {Boolean} Returns true if cas number and chemical name are empty.
   */
  const hasCasAndChemEmptyError = (responseData) => {
    // if data has some rows with missing cas and chem message, return true
    const casNumMessages = responseData.every(
      (row) => row.messages?.casNumber?.[0]?.errorId === "cas_and_chem_empty",
    );
    const chemNameMessages = responseData.every(
      (row) =>
        row.messages?.chemicalName?.[0]?.errorId === "cas_and_chem_empty",
    );
    return casNumMessages && chemNameMessages;
  };

  // Register a hook to manipulate the data and remove line breaks, and also
  // validate the chemical container import data.
  dromo.registerBulkRowHook((records, mode) => {
    dromoFinished.value = false;
    if (mode === "init") {
      allRecords = records;
    }
    const fields = dromoOptions.fields?.map((field) => field.key);
    records = records?.map((record) => {
      const newRecord = record;
      const processed = replaceLineBreaks(fields, newRecord);
      return processed;
    });
    Promise.resolve(validateData(records)).then((response) => {
      if (!response?.data?.length) {
        // Dromo support suggests using alert for our need. The alert shows up in the Dromo UI.
        // eslint-disable-next-line no-alert
        alert(
          "Error when trying to validate. Please click 'Go Back' and re-confirm your column matching.",
        );
      } else {
        if (hasCasAndChemEmptyError(response?.data)) {
          // eslint-disable-next-line no-alert
          alert(
            "You must match a column to Chemical Name or CAS Number. Please click 'Go Back' and change your column matching or upload a CSV that contains at least one of those columns.",
          );
        }
        const messagesInResponse = handleValidationResponse(response?.data);
        // Get updated list of messages and set empty array to old messages
        const fullListOfMessages = updateFullListOfMessages(
          records,
          messagesInResponse,
        );
        dromo.updateInfoMessages(fullListOfMessages);
      }
    });
    return records;
  });

  dromo.onResults((data, metadata) => {
    dromoResultsData.value = data;
    dromoResultsMetadata.value = metadata;
    dromoFinished.value = true;
    Promise.resolve(validateData(data, true));
  });

  return {
    dromoOptions,
    dromo,
    dromoResultsData,
    dromoResultsMetadata,
    dromoFinished,
    replaceLineBreaks,
    restructureImportData,
    handleValidationResponse,
    validateData,
    updateFullListOfMessages,
    hasCasAndChemEmptyError,
    isBusyValidating,
  };
}
